import type { SidebarSectionGroup } from '@/elements/research/components/sidebarSections';

// Bump when SidebarLayoutState's shape changes in a way old persisted data
// can't be read as-is. getStoredSidebarLayout() wipes and falls back to
// DEFAULT_SIDEBAR_LAYOUT_STATE on any version mismatch or shape failure — see
// sidebarLayoutSlice.ts. No migration path exists yet since this is v1.
export const SIDEBAR_LAYOUT_SCHEMA_VERSION = 1;

// Custom (user-created) groups get a `custom:` prefixed id so they can never
// collide with a built-in SidebarSectionGroup literal.
export type CustomGroupId = `custom:${string}`;
export type LayoutGroupId = SidebarSectionGroup | CustomGroupId;

export const isCustomGroupId = (id: LayoutGroupId): id is CustomGroupId =>
  id.startsWith('custom:');

export interface CustomGroupDef {
  id: CustomGroupId;
  // User-typed, rendered verbatim — no i18n key (unlike built-in group labels).
  label: string;
}

// Per-item override. Every field is independently optional (a "sparse"
// override) — e.g. hiding an item doesn't require also knowing its resolved
// group/order, and moving an item doesn't require touching its hidden flag.
// Absent fields fall back to the section's `sidebarSections.tsx` default at
// resolve time (see resolveSidebarLayout in hooks/useSidebarLayout.ts).
export interface SidebarItemOverride {
  group?: LayoutGroupId;
  order?: number;
  hidden?: boolean;
}

export interface SidebarLayoutState {
  version: number;
  // Renames of built-in groups (including 'top', the otherwise-unlabeled
  // primary cluster). Empty string is treated as "not renamed" — falls back
  // to the default i18n label — so a cleared input can't leave a blank header.
  groupRenames: Partial<Record<SidebarSectionGroup, string>>;
  customGroups: CustomGroupDef[];
  // Display order of ALL groups (built-in ids + custom ids). Any group id
  // not listed here is appended in a stable fallback order (built-ins in
  // their catalog order, then custom groups in creation order) — this is a
  // preference, not the source of truth for group membership.
  groupOrder: LayoutGroupId[];
  // Sparse per-item overrides, keyed by SidebarSectionKey. Kept as `string`
  // (not the narrower union) so a section key removed/renamed in a future
  // release still parses safely — the merge logic only ever looks these up
  // by iterating CURRENT SIDEBAR_SECTIONS, so a stale key is simply orphaned
  // and inert, never crashes and never renders a phantom row.
  items: Record<string, SidebarItemOverride>;
}

export const DEFAULT_SIDEBAR_LAYOUT_STATE: SidebarLayoutState = {
  version: SIDEBAR_LAYOUT_SCHEMA_VERSION,
  groupRenames: {},
  customGroups: [],
  groupOrder: [],
  items: {},
};
